import { useState } from 'react';
import { cn } from '@/lib/cn';
import { timeAgo } from '@/lib/format';
import { useAdminKycList } from '@/hooks/use-admin-kyc';
import { useSectionPermission } from '@/hooks/use-admin-permissions';
import type { KycStatus } from '@shared/types/api';
import { KycReviewDialog } from './kyc-review-dialog';

const TABS: { value: KycStatus | 'ALL'; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'ALL', label: 'All' },
];

const STATUS_TONE: Record<KycStatus, string> = {
  NOT_SUBMITTED: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-amber-50 text-amber-800',
  VERIFIED: 'bg-brand-50 text-brand-800',
  REJECTED: 'bg-red-50 text-red-700',
};

export function AdminKycPage() {
  const [tab, setTab] = useState<KycStatus | 'ALL'>('PENDING');
  const [selectedKycId, setSelectedKycId] = useState<string | null>(null);
  const { canEdit, canDelete, isSuperAdmin } = useSectionPermission('KYC');
  const { data, isLoading } = useAdminKycList({
    status: tab === 'ALL' ? undefined : tab,
    pageSize: 50,
  });

  const items = data?.items ?? [];

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <h1 className="text-xl font-semibold text-gray-900">KYC Review</h1>
      <p className="text-sm text-gray-600">
        Approve or reject Associate KYC submissions. Only VERIFIED associates
        can be assigned client transactions.
      </p>

      <div className="mt-5 flex items-center gap-1 overflow-x-auto no-scrollbar scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'shrink-0 -mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap',
              tab === t.value
                ? 'border-brand-700 text-brand-800'
                : 'border-transparent text-gray-600 hover:text-gray-900',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="mt-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[650px] text-sm text-left">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Associate</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Profession</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
              <th className="px-4 py-3 font-medium whitespace-nowrap">Submitted</th>
              <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-6 text-center text-gray-500">
                  No submissions{tab !== 'ALL' ? ` in ${tab} state` : ''}.
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const status = row.user.associateProfile?.kycStatus ?? 'NOT_SUBMITTED';
                const name =
                  `${row.user.firstName ?? ''} ${row.user.lastName ?? ''}`.trim() ||
                  row.user.email;
                return (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <div className="font-medium text-gray-900">{name}</div>
                      <div className="text-xs text-gray-500">{row.user.email}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap">
                      {row.user.associateProfile?.profession.replace(/_/g, ' ').toLowerCase() ??
                        '—'}
                      {row.user.associateProfile?.isFranchise ? (
                        <span className="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600">
                          Franchise
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase',
                          STATUS_TONE[status],
                        )}
                      >
                        {status}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700">
                      {timeAgo(row.submittedAt)}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <button
                        onClick={() => setSelectedKycId(row.id)}
                        className="text-sm text-brand-700 hover:underline"
                      >
                        {(canEdit || canDelete || isSuperAdmin) ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </section>

      <KycReviewDialog
        kycId={selectedKycId}
        open={selectedKycId !== null}
        onClose={() => setSelectedKycId(null)}
      />
    </div>
  );
}
